import React from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { User, LogIn } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import BookingSummary from './BookingSummary';

const FIELDS = [
  { key: 'customer_name', label: 'FULL NAME *', type: 'text', placeholder: 'Your full name' },
  { key: 'customer_email', label: 'EMAIL *', type: 'email', placeholder: 'your@email.com' },
  { key: 'customer_phone', label: 'PHONE', type: 'tel', placeholder: 'Your phone number' },
];

const inputCls = "bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";

export default function BookingStepDetails({ formData, onChange, experienceType, selectedDate, timeSlot, loggedInUser }) {
  return (
    <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-5">
          {loggedInUser ? (
            <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 px-4 py-2.5">
              <User className="w-3.5 h-3.5 text-primary" />
              <span className="text-primary text-[10px] font-bold tracking-wider">SIGNED IN AS {loggedInUser.full_name?.toUpperCase() || loggedInUser.email?.toUpperCase()}</span>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-[#111115] border border-white/10 px-4 py-3">
              <p className="text-white/40 text-sm">Sign in to auto-fill your details</p>
              <button
                onClick={() => base44.auth.redirectToLogin(window.location.pathname)}
                className="flex items-center gap-2 text-primary text-xs font-bold tracking-wider hover:text-primary/80 transition-colors"
              >
                <LogIn className="w-4 h-4" /> SIGN IN
              </button>
            </div>
          )}
          <p className="text-white/40 text-sm">Enter your contact details to complete the booking.</p>
          {FIELDS.map((field) => (
            <div key={field.key}>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">{field.label}</Label>
              <Input
                type={field.type}
                value={formData[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
                className={inputCls}
                placeholder={field.placeholder}
              />
            </div>
          ))}
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">NOTES</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              className="bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60"
              placeholder="Any special requirements or requests..."
              rows={3}
            />
          </div>
          <div>
            <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">HOW DID YOU HEAR ABOUT US?</Label>
            <select
              value={formData.referral_source || ''}
              onChange={(e) => onChange('referral_source', e.target.value)}
              className={`w-full bg-[#111115] border border-white/10 text-white h-11 px-3 focus:border-primary/60 focus:outline-none ${formData.referral_source ? '' : 'text-white/40'}`}
            >
              <option value="">Select an option…</option>
              <option value="tripadvisor">TripAdvisor</option>
              <option value="google">Google</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="tiktok">TikTok</option>
              <option value="word_of_mouth">Friend / Family</option>
              <option value="returning_customer">Returning customer</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="lg:col-span-1">
          <BookingSummary experienceType={experienceType} selectedDate={selectedDate} timeSlot={timeSlot} />
        </div>
      </div>
    </motion.div>
  );
}